import type {
  PostCondition,
  PostFilter,
  PostStatCondition,
  PostStatFilter,
} from "@/graphql/codegen/graphql";
import { PostsOrderBy, PostStatsOrderBy } from "@/graphql/codegen/graphql";
import { AssociationReactionValueEmojiMap } from "@/utils/constants";
import { isDesoPublicKey } from "@/utils/helpers";

export type FeedQueryPresetId =
  | "trending"
  | "new"
  | "images"
  | "videos"
  | "audio"
  | "nfts";

export type FeedQueryPresetDefinition = {
  id: FeedQueryPresetId;
  label: string;
  description: string;
  orderBy: PostsOrderBy | PostsOrderBy[];
  allowCustomOrderBy?: boolean;
  buildFilter: (options?: { now?: Date }) => PostFilter | undefined;
};

export type CustomFeedQuerySource = "posts" | "post-stats";
export type CustomFeedQuerySortValue = PostsOrderBy | PostStatsOrderBy;
export type CustomFeedQuerySort = CustomFeedQuerySortValue[];

export type CustomFeedRelativeTimeframeUnit =
  | "minutes"
  | "hours"
  | "days"
  | "weeks"
  | "months"
  | "years";

export type CustomFeedTimeframe =
  | {
      mode: "anytime";
    }
  | {
      mode: "relative";
      amount: number;
      unit: CustomFeedRelativeTimeframeUnit;
    }
  | {
      mode: "range";
      from: string;
      to: string;
    };

export type CustomFeedQueryAuthor = {
  publicKey: string;
  username?: string;
};

export type CustomFeedQueryRuleOperator =
  | "equalTo"
  | "notEqualTo"
  | "greaterThan"
  | "greaterThanOrEqualTo"
  | "lessThan"
  | "lessThanOrEqualTo";

export type CustomFeedQueryRuleField =
  | "isQuotedRepost"
  | "isPinned"
  | "hasUnlockable"
  | "subscriptionAccessGroupExists"
  | "upvoteCappedWealthUsdCents"
  | "totalDiamondValueUsdCents"
  | "posterTotalBalanceUsdCents"
  | "commentDepth"
  | "postStats.angryReactionCount"
  | "postStats.astonishedReactionCount"
  | "postStats.diamondsLevelSum"
  | "postStats.totalReactionCount"
  | "postStats.positiveReactionCount"
  | "postStats.negativeReactionCount"
  | "postStats.dislikeReactionCount"
  | "postStats.lastDiamondUpdateBlockHeight"
  | "postStats.lastReactionUpdateBlockHeight"
  | "postStats.laughReactionCount"
  | "postStats.likeReactionCount"
  | "postStats.loveReactionCount"
  | "postStats.replyCount"
  | "postStats.viewCount"
  | "postStats.whaleViewCount"
  | "postStats.quoteCount"
  | "postStats.repostCount"
  | "postStats.sadReactionCount"
  | "postStats.diamondsTotalCount"
  | "postStats.diamondsTotalUsdCents"
  | "postStats.upvoteWealthTotalUsdCents"
  | "postStats.upvoteWealthTotalUsdCentsCapped";

export type CustomFeedQueryRule = {
  id: string;
  field: CustomFeedQueryRuleField;
  operator: CustomFeedQueryRuleOperator;
  value: boolean | number;
};

export type CustomFeedQueryLogicalOperator = "and" | "or";

export type CustomFeedQueryRuleSection = {
  id: string;
  joinWithPrevious?: CustomFeedQueryLogicalOperator;
  matchMode: CustomFeedQueryLogicalOperator;
  rules: CustomFeedQueryRule[];
};

export type CustomFeedQueryConfig = {
  author?: CustomFeedQueryAuthor;
  keyword: string;
  sortBy: CustomFeedQuerySort;
  timeframe: CustomFeedTimeframe;
  includeReposts: boolean;
  includeQuotes: boolean;
  hasImages: boolean;
  hasVideos: boolean;
  hasAudio: boolean;
  isGated: boolean;
  isNft: boolean;
  advancedSections: CustomFeedQueryRuleSection[];
};

export const CUSTOM_FEED_KEYWORD_MAX_LENGTH = 64;
export const CUSTOM_FEED_MAX_RELATIVE_TIMEFRAME_AMOUNT = 9999;
export const CUSTOM_FEED_MAX_SORT_FIELDS = 5;
export type CustomFeedSortDirection = "asc" | "desc";
const LU_ICON_PREFIX = "lu:";
const CUSTOM_FEED_KEYWORD_TOKEN_PATTERN = /[a-zA-Z0-9_-]+/;
const CUSTOM_FEED_KEYWORD_BOUNDARY_CHARACTERS = [
  " ",
  "\n",
  "\t",
  ".",
  ",",
  "!",
  "?",
  ":",
  ";",
  "(",
  ")",
  "[",
  "]",
  "{",
  "}",
  '"',
  "'",
  "/",
  "#",
  "@",
];

const toLucideIconToken = (value: string) => `${LU_ICON_PREFIX}${value}`;
export const CUSTOM_FEED_SORT_OPTIONS: Array<{
  label: string;
  value: CustomFeedQuerySortValue;
  source: CustomFeedQuerySource;
  icon: string;
  direction?: CustomFeedSortDirection;
}> = [];
export const CUSTOM_FEED_EDITOR_HIDDEN_SORTS =
  new Set<CustomFeedQuerySortValue>([
    PostsOrderBy.UpvoteCappedWealthUsdCentsAsc,
    PostsOrderBy.UpvoteCappedWealthUsdCentsDesc,
    PostsOrderBy.TotalDiamondValueUsdCentsAsc,
    PostsOrderBy.TotalDiamondValueUsdCentsDesc,
    PostsOrderBy.DiamondCountAsc,
    PostsOrderBy.DiamondCountDesc,
    PostsOrderBy.DiamondLevelSumAsc,
    PostsOrderBy.DiamondLevelSumDesc,
    PostsOrderBy.PosterTotalBalanceUsdCentsAsc,
    PostsOrderBy.PosterTotalBalanceUsdCentsDesc,
    PostsOrderBy.PosterDesoBalanceUsdCentsAsc,
    PostsOrderBy.PosterDesoBalanceUsdCentsDesc,
    PostsOrderBy.PosterFocusTokenBalanceUsdCentsAsc,
    PostsOrderBy.PosterFocusTokenBalanceUsdCentsDesc,
    PostsOrderBy.PosterUsdBalanceUsdCentsAsc,
    PostsOrderBy.PosterUsdBalanceUsdCentsDesc,
    PostsOrderBy.CommentDepthAsc,
    PostsOrderBy.CommentDepthDesc,
    PostsOrderBy.IsQuotedRepostAsc,
    PostsOrderBy.IsQuotedRepostDesc,
    PostsOrderBy.IsNftAsc,
    PostsOrderBy.IsNftDesc,
    PostsOrderBy.HasUnlockableAsc,
    PostsOrderBy.HasUnlockableDesc,
    PostsOrderBy.BodyAsc,
    PostsOrderBy.BodyDesc,
    PostsOrderBy.PosterPublicKeyAsc,
    PostsOrderBy.PosterPublicKeyDesc,
    PostsOrderBy.PostHashAsc,
    PostsOrderBy.PostHashDesc,
    PostsOrderBy.ParentPostHashAsc,
    PostsOrderBy.ParentPostHashDesc,
    PostsOrderBy.RootPostHashAsc,
    PostsOrderBy.RootPostHashDesc,
    PostsOrderBy.RepostedPostHashAsc,
    PostsOrderBy.RepostedPostHashDesc,
    PostsOrderBy.ImageUrlsAsc,
    PostsOrderBy.ImageUrlsDesc,
    PostsOrderBy.VideoUrlsAsc,
    PostsOrderBy.VideoUrlsDesc,
    PostsOrderBy.IsHiddenAsc,
    PostsOrderBy.IsHiddenDesc,
    PostsOrderBy.IsPinnedAsc,
    PostsOrderBy.IsPinnedDesc,
    PostsOrderBy.SubscriptionAccessGroupKeyNameAsc,
    PostsOrderBy.SubscriptionAccessGroupKeyNameDesc,
    PostsOrderBy.SubscriptionAccessGroupOwnerPublicKeyAsc,
    PostsOrderBy.SubscriptionAccessGroupOwnerPublicKeyDesc,
    PostsOrderBy.BadgerKeyAsc,
    PostsOrderBy.BadgerKeyDesc,
    PostsOrderBy.IsFrozenAsc,
    PostsOrderBy.IsFrozenDesc,
    PostsOrderBy.ExtraDataAsc,
    PostsOrderBy.ExtraDataDesc,
    PostsOrderBy.Natural,
    PostStatsOrderBy.UpvoteWealthTotalUsdCentsCappedAsc,
    PostStatsOrderBy.UpvoteWealthTotalUsdCentsCappedDesc,
    PostStatsOrderBy.LastDiamondUpdateBlockHeightAsc,
    PostStatsOrderBy.LastDiamondUpdateBlockHeightDesc,
    PostStatsOrderBy.LastReactionUpdateBlockHeightAsc,
    PostStatsOrderBy.LastReactionUpdateBlockHeightDesc,
  ]);
export const CUSTOM_FEED_EDITOR_SORT_OPTIONS: Array<{
  label: string;
  value: CustomFeedQuerySortValue;
  source: CustomFeedQuerySource;
  icon: string;
  direction?: CustomFeedSortDirection;
}> = [];
export const CUSTOM_FEED_RELATIVE_TIMEFRAME_UNIT_OPTIONS: Array<{
  label: string;
  value: CustomFeedRelativeTimeframeUnit;
}> = [
  {
    label: "Minutes",
    value: "minutes",
  },
  {
    label: "Hours",
    value: "hours",
  },
  {
    label: "Days",
    value: "days",
  },
  {
    label: "Weeks",
    value: "weeks",
  },
  {
    label: "Months",
    value: "months",
  },
  {
    label: "Years",
    value: "years",
  },
];
export const CUSTOM_FEED_RULE_MATCH_MODE_OPTIONS: Array<{
  label: string;
  value: CustomFeedQueryLogicalOperator;
}> = [
  {
    label: "AND",
    value: "and",
  },
  {
    label: "OR",
    value: "or",
  },
];

export const DEFAULT_CUSTOM_FEED_QUERY_CONFIG: CustomFeedQueryConfig = {
  keyword: "",
  sortBy: [PostsOrderBy.TimestampDesc],
  timeframe: {
    mode: "anytime",
  },
  includeReposts: true,
  includeQuotes: true,
  hasImages: false,
  hasVideos: false,
  hasAudio: false,
  isGated: false,
  isNft: false,
  advancedSections: [],
};

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const ONE_WEEK_IN_MS = 7 * ONE_DAY_IN_MS;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === "object" && !Array.isArray(value);
};

const hasNonEmptyFilterValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === "object") {
    return Object.values(value).some((entry) => hasNonEmptyFilterValue(entry));
  }

  return value !== undefined;
};

const isNonEmptyPostFilter = (
  filter: PostFilter | undefined,
): filter is PostFilter => {
  return hasNonEmptyFilterValue(filter);
};

const combinePostFilters = (
  ...filters: Array<PostFilter | undefined>
): PostFilter | undefined => {
  const validFilters = filters.filter(isNonEmptyPostFilter);

  if (validFilters.length === 0) {
    return undefined;
  }

  if (validFilters.length === 1) {
    return validFilters[0];
  }

  return {
    and: validFilters,
  };
};

const getRecentTimestamp = ({
  now = new Date(),
  days = 7,
}: {
  now?: Date;
  days?: number;
}) => {
  return new Date(now.getTime() - days * ONE_DAY_IN_MS).toISOString();
};

const TRENDING_ENGAGEMENT_FILTER: PostFilter = {
  postStats: {
    or: [
      {
        diamondsTotalUsdCents: {
          greaterThan: 0,
        },
      },
      {
        upvoteWealthTotalUsdCentsCapped: {
          greaterThan: 0,
        },
      },
      {
        viewCount: {
          greaterThan: 0,
        },
      },
      {
        totalReactionCount: {
          greaterThan: 0,
        },
      },
      {
        replyCount: {
          greaterThan: 0,
        },
      },
    ],
  },
};

const IMAGES_FILTER: PostFilter = {
  or: [
    {
      imageUrls: {
        anyNotEqualTo: "",
      },
    },
    {
      extraData: {
        containsKey: "SubscriptionEncryptedImageURLs",
      },
    },
  ],
};

const VIDEOS_FILTER: PostFilter = {
  or: [
    {
      videoUrls: {
        anyNotEqualTo: "",
      },
    },
    {
      extraData: {
        containsKey: "SubscriptionEncryptedVideoURLs",
      },
    },
  ],
};

const AUDIO_FILTER: PostFilter = {
  extraData: {
    containsAnyKeys: ["AudioURLs", "SubscriptionEncryptedAudioURLs"],
  },
};

const GATED_FILTER: PostFilter = {
  or: [
    {
      subscriptionAccessGroupExists: true,
    },
    {
      hasUnlockable: {
        equalTo: true,
      },
    },
  ],
};

const getCustomFeedRepostFilter = ({
  includeQuotes,
  includeReposts,
}: Pick<CustomFeedQueryConfig, "includeQuotes" | "includeReposts">):
  | PostFilter
  | undefined => {
  if (includeReposts && includeQuotes) {
    return undefined;
  }

  if (!includeReposts && !includeQuotes) {
    return {
      repostedPostHash: {
        isNull: true,
      },
    };
  }

  if (!includeReposts) {
    return {
      or: [
        {
          repostedPostHash: {
            isNull: true,
          },
        },
        {
          isQuotedRepost: {
            equalTo: true,
          },
        },
      ],
    };
  }

  return {
    isQuotedRepost: {
      equalTo: false,
    },
  };
};

const NFTS_FILTER: PostFilter = {
  isNft: {
    equalTo: true,
  },
};

export const sanitizeCustomFeedKeyword = (value: unknown) => {
  if (typeof value !== "string") {
    return DEFAULT_CUSTOM_FEED_QUERY_CONFIG.keyword;
  }

  return (value.match(CUSTOM_FEED_KEYWORD_TOKEN_PATTERN)?.[0] ?? "").slice(
    0,
    CUSTOM_FEED_KEYWORD_MAX_LENGTH,
  );
};

const escapeLikePattern = (value: string) => {
  return value.replace(/[\\%_]/g, "\\$&");
};

export const buildCustomFeedKeywordPostFilter = (
  keyword: string,
): PostFilter | undefined => {
  if (!keyword) {
    return undefined;
  }

  const escapedKeyword = escapeLikePattern(keyword);
  const boundaryFilters = CUSTOM_FEED_KEYWORD_BOUNDARY_CHARACTERS.flatMap(
    (leftBoundary) => {
      return CUSTOM_FEED_KEYWORD_BOUNDARY_CHARACTERS.map((rightBoundary) => ({
        body: {
          likeInsensitive: `%${escapeLikePattern(leftBoundary)}${escapedKeyword}${escapeLikePattern(rightBoundary)}%`,
        },
      }));
    },
  );

  // GraphQL exposes SQL LIKE, not regex word boundaries. This finite boundary
  // list keeps the keyword token from matching the middle of another word.
  return {
    or: [
      {
        body: {
          equalToInsensitive: keyword,
        },
      },
      ...CUSTOM_FEED_KEYWORD_BOUNDARY_CHARACTERS.map((boundary) => ({
        body: {
          startsWithInsensitive: `${keyword}${boundary}`,
        },
      })),
      ...CUSTOM_FEED_KEYWORD_BOUNDARY_CHARACTERS.map((boundary) => ({
        body: {
          endsWithInsensitive: `${boundary}${keyword}`,
        },
      })),
      ...boundaryFilters,
    ],
  };
};

const clampRelativeTimeframeAmount = (value: unknown) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 1;
  }

  return Math.max(
    1,
    Math.min(CUSTOM_FEED_MAX_RELATIVE_TIMEFRAME_AMOUNT, Math.round(value)),
  );
};

const POSTS_ORDER_BY_SPECIAL_LABELS: Partial<Record<PostsOrderBy, string>> = {
  [PostsOrderBy.Natural]: "Natural",
  [PostsOrderBy.TimestampDesc]: "Timestamp",
  [PostsOrderBy.TimestampAsc]: "Timestamp",
};
const POST_STATS_ORDER_BY_SPECIAL_LABELS: Partial<
  Record<PostStatsOrderBy, string>
> = {
  [PostStatsOrderBy.AngryReactionCountDesc]: "Angry Reactions",
  [PostStatsOrderBy.AngryReactionCountAsc]: "Angry Reactions",
  [PostStatsOrderBy.AstonishedReactionCountDesc]: "Astonished Reactions",
  [PostStatsOrderBy.AstonishedReactionCountAsc]: "Astonished Reactions",
  [PostStatsOrderBy.DislikeReactionCountDesc]: "Dislike Reactions",
  [PostStatsOrderBy.DislikeReactionCountAsc]: "Dislike Reactions",
  [PostStatsOrderBy.LaughReactionCountDesc]: "Laugh Reactions",
  [PostStatsOrderBy.LaughReactionCountAsc]: "Laugh Reactions",
  [PostStatsOrderBy.LikeReactionCountDesc]: "Like Reactions",
  [PostStatsOrderBy.LikeReactionCountAsc]: "Like Reactions",
  [PostStatsOrderBy.LoveReactionCountDesc]: "Love Reactions",
  [PostStatsOrderBy.LoveReactionCountAsc]: "Love Reactions",
  [PostStatsOrderBy.TotalReactionCountDesc]: "Total Reactions",
  [PostStatsOrderBy.TotalReactionCountAsc]: "Total Reactions",
  [PostStatsOrderBy.PositiveReactionCountDesc]: "Positive Reactions",
  [PostStatsOrderBy.PositiveReactionCountAsc]: "Positive Reactions",
  [PostStatsOrderBy.NegativeReactionCountDesc]: "Negative Reactions",
  [PostStatsOrderBy.NegativeReactionCountAsc]: "Negative Reactions",
  [PostStatsOrderBy.ReplyCountDesc]: "Replies",
  [PostStatsOrderBy.ReplyCountAsc]: "Replies",
  [PostStatsOrderBy.ViewCountDesc]: "Views",
  [PostStatsOrderBy.ViewCountAsc]: "Views",
  [PostStatsOrderBy.WhaleViewCountDesc]: "Whale Views",
  [PostStatsOrderBy.WhaleViewCountAsc]: "Whale Views",
  [PostStatsOrderBy.QuoteCountDesc]: "Quotes",
  [PostStatsOrderBy.QuoteCountAsc]: "Quotes",
  [PostStatsOrderBy.RepostCountDesc]: "Reposts",
  [PostStatsOrderBy.RepostCountAsc]: "Reposts",
  [PostStatsOrderBy.SadReactionCountDesc]: "Sad Reactions",
  [PostStatsOrderBy.SadReactionCountAsc]: "Sad Reactions",
  [PostStatsOrderBy.DiamondsLevelSumDesc]: "Diamonds Level Sum",
  [PostStatsOrderBy.DiamondsLevelSumAsc]: "Diamonds Level Sum",
  [PostStatsOrderBy.DiamondsTotalUsdCentsDesc]: "Diamond Value",
  [PostStatsOrderBy.DiamondsTotalUsdCentsAsc]: "Diamond Value",
  [PostStatsOrderBy.UpvoteWealthTotalUsdCentsDesc]: "Upvote Value",
  [PostStatsOrderBy.UpvoteWealthTotalUsdCentsAsc]: "Upvote Value",
};

type CustomFeedRuleValueType = "boolean" | "number";
type CustomFeedRuleTarget = "root-filter" | "root-exists" | "post-stats";
type CustomFeedRuleDefinition = {
  value: CustomFeedQueryRuleField;
  label: string;
  target: CustomFeedRuleTarget;
  filterField: string;
  postConditionField?: keyof PostCondition;
  postStatConditionField?: keyof PostStatCondition;
  operators: CustomFeedQueryRuleOperator[];
  valueType: CustomFeedRuleValueType;
  defaultValue: boolean | number;
};

const NUMERIC_RULE_OPERATORS: CustomFeedQueryRuleOperator[] = [
  "greaterThanOrEqualTo",
  "greaterThan",
  "equalTo",
  "lessThanOrEqualTo",
  "lessThan",
  "notEqualTo",
];

const BOOLEAN_RULE_OPERATORS: CustomFeedQueryRuleOperator[] = [
  "equalTo",
  "notEqualTo",
];

const ROOT_BOOLEAN_RULES: CustomFeedRuleDefinition[] = [
  {
    value: "isQuotedRepost",
    label: "Is Quoted Repost",
    target: "root-filter",
    filterField: "isQuotedRepost",
    postConditionField: "isQuotedRepost",
    operators: BOOLEAN_RULE_OPERATORS,
    valueType: "boolean",
    defaultValue: true,
  },
  {
    value: "isPinned",
    label: "Is Pinned",
    target: "root-filter",
    filterField: "isPinned",
    postConditionField: "isPinned",
    operators: BOOLEAN_RULE_OPERATORS,
    valueType: "boolean",
    defaultValue: true,
  },
  {
    value: "hasUnlockable",
    label: "Has Unlockable",
    target: "root-filter",
    filterField: "hasUnlockable",
    postConditionField: "hasUnlockable",
    operators: BOOLEAN_RULE_OPERATORS,
    valueType: "boolean",
    defaultValue: true,
  },
  {
    value: "subscriptionAccessGroupExists",
    label: "Has Subscription Gate",
    target: "root-exists",
    filterField: "subscriptionAccessGroupExists",
    operators: BOOLEAN_RULE_OPERATORS,
    valueType: "boolean",
    defaultValue: true,
  },
];

const ROOT_NUMERIC_RULES: CustomFeedRuleDefinition[] = [
  {
    value: "upvoteCappedWealthUsdCents",
    label: "Upvote Value (cents)",
    target: "root-filter",
    filterField: "upvoteCappedWealthUsdCents",
    postConditionField: "upvoteCappedWealthUsdCents",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 10000,
  },
  {
    value: "totalDiamondValueUsdCents",
    label: "Diamond Value (cents)",
    target: "root-filter",
    filterField: "totalDiamondValueUsdCents",
    postConditionField: "totalDiamondValueUsdCents",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1000,
  },
  {
    value: "posterTotalBalanceUsdCents",
    label: "Author Balance (cents)",
    target: "root-filter",
    filterField: "posterTotalBalanceUsdCents",
    postConditionField: "posterTotalBalanceUsdCents",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 100000,
  },
  {
    value: "commentDepth",
    label: "Comment Depth",
    target: "root-filter",
    filterField: "commentDepth",
    postConditionField: "commentDepth",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 0,
  },
];

const POST_STATS_RULES: CustomFeedRuleDefinition[] = [
  {
    value: "postStats.angryReactionCount",
    label: "Angry Reactions",
    target: "post-stats",
    filterField: "angryReactionCount",
    postStatConditionField: "angryReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.astonishedReactionCount",
    label: "Astonished Reactions",
    target: "post-stats",
    filterField: "astonishedReactionCount",
    postStatConditionField: "astonishedReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.diamondsLevelSum",
    label: "Diamonds Level Sum",
    target: "post-stats",
    filterField: "diamondsLevelSum",
    postStatConditionField: "diamondsLevelSum",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.totalReactionCount",
    label: "Total Reactions",
    target: "post-stats",
    filterField: "totalReactionCount",
    postStatConditionField: "totalReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 10,
  },
  {
    value: "postStats.positiveReactionCount",
    label: "Positive Reactions",
    target: "post-stats",
    filterField: "positiveReactionCount",
    postStatConditionField: "positiveReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 5,
  },
  {
    value: "postStats.negativeReactionCount",
    label: "Negative Reactions",
    target: "post-stats",
    filterField: "negativeReactionCount",
    postStatConditionField: "negativeReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.dislikeReactionCount",
    label: "Dislike Reactions",
    target: "post-stats",
    filterField: "dislikeReactionCount",
    postStatConditionField: "dislikeReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.lastDiamondUpdateBlockHeight",
    label: "Last Diamond Update Block Height",
    target: "post-stats",
    filterField: "lastDiamondUpdateBlockHeight",
    postStatConditionField: "lastDiamondUpdateBlockHeight",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 0,
  },
  {
    value: "postStats.lastReactionUpdateBlockHeight",
    label: "Last Reaction Update Block Height",
    target: "post-stats",
    filterField: "lastReactionUpdateBlockHeight",
    postStatConditionField: "lastReactionUpdateBlockHeight",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 0,
  },
  {
    value: "postStats.laughReactionCount",
    label: "Laugh Reactions",
    target: "post-stats",
    filterField: "laughReactionCount",
    postStatConditionField: "laughReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.likeReactionCount",
    label: "Like Reactions",
    target: "post-stats",
    filterField: "likeReactionCount",
    postStatConditionField: "likeReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.loveReactionCount",
    label: "Love Reactions",
    target: "post-stats",
    filterField: "loveReactionCount",
    postStatConditionField: "loveReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.replyCount",
    label: "Reply Count",
    target: "post-stats",
    filterField: "replyCount",
    postStatConditionField: "replyCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 5,
  },
  {
    value: "postStats.viewCount",
    label: "View Count",
    target: "post-stats",
    filterField: "viewCount",
    postStatConditionField: "viewCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 100,
  },
  {
    value: "postStats.whaleViewCount",
    label: "Whale View Count",
    target: "post-stats",
    filterField: "whaleViewCount",
    postStatConditionField: "whaleViewCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 10,
  },
  {
    value: "postStats.quoteCount",
    label: "Quote Count",
    target: "post-stats",
    filterField: "quoteCount",
    postStatConditionField: "quoteCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.repostCount",
    label: "Repost Count",
    target: "post-stats",
    filterField: "repostCount",
    postStatConditionField: "repostCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.sadReactionCount",
    label: "Sad Reactions",
    target: "post-stats",
    filterField: "sadReactionCount",
    postStatConditionField: "sadReactionCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.diamondsTotalCount",
    label: "Diamonds Count",
    target: "post-stats",
    filterField: "diamondsTotalCount",
    postStatConditionField: "diamondsTotalCount",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1,
  },
  {
    value: "postStats.diamondsTotalUsdCents",
    label: "Diamonds Value (stats cents)",
    target: "post-stats",
    filterField: "diamondsTotalUsdCents",
    postStatConditionField: "diamondsTotalUsdCents",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 1000,
  },
  {
    value: "postStats.upvoteWealthTotalUsdCents",
    label: "Upvote Value (stats cents)",
    target: "post-stats",
    filterField: "upvoteWealthTotalUsdCents",
    postStatConditionField: "upvoteWealthTotalUsdCents",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 10000,
  },
  {
    value: "postStats.upvoteWealthTotalUsdCentsCapped",
    label: "Upvote Value (stats capped cents)",
    target: "post-stats",
    filterField: "upvoteWealthTotalUsdCentsCapped",
    postStatConditionField: "upvoteWealthTotalUsdCentsCapped",
    operators: NUMERIC_RULE_OPERATORS,
    valueType: "number",
    defaultValue: 10000,
  },
];

const CUSTOM_FEED_RULE_DEFINITIONS: CustomFeedRuleDefinition[] = [
  ...ROOT_BOOLEAN_RULES,
  ...ROOT_NUMERIC_RULES,
  ...POST_STATS_RULES,
];

const CUSTOM_FEED_RULE_DEFINITION_BY_VALUE = Object.fromEntries(
  CUSTOM_FEED_RULE_DEFINITIONS.map((definition) => [
    definition.value,
    definition,
  ]),
) as Record<CustomFeedQueryRuleField, CustomFeedRuleDefinition>;

export const CUSTOM_FEED_HIDDEN_RULE_FIELDS = new Set<CustomFeedQueryRuleField>(
  [
    "isQuotedRepost",
    "isPinned",
    "hasUnlockable",
    "postStats.lastDiamondUpdateBlockHeight",
    "postStats.lastReactionUpdateBlockHeight",
  ],
);

export const CUSTOM_FEED_RULE_FIELD_OPTIONS =
  CUSTOM_FEED_RULE_DEFINITIONS.filter((definition) => {
    return !CUSTOM_FEED_HIDDEN_RULE_FIELDS.has(definition.value);
  }).map((definition) => ({
    icon: getCustomFeedRuleFieldIcon(definition.value),
    label: definition.label,
    value: definition.value,
  }));

export const CUSTOM_FEED_RULE_OPERATOR_LABELS: Record<
  CustomFeedQueryRuleOperator,
  string
> = {
  equalTo: "is equal to",
  notEqualTo: "is not equal to",
  greaterThan: "is greater than",
  greaterThanOrEqualTo: "is at least",
  lessThan: "is less than",
  lessThanOrEqualTo: "is at most",
};

export const CUSTOM_FEED_RULE_OPERATOR_SYMBOLS: Record<
  CustomFeedQueryRuleOperator,
  string
> = {
  equalTo: "=",
  notEqualTo: "!=",
  greaterThan: ">",
  greaterThanOrEqualTo: ">=",
  lessThan: "<",
  lessThanOrEqualTo: "<=",
};

export const getCustomFeedRuleDefinition = (
  field: CustomFeedQueryRuleField,
) => {
  return CUSTOM_FEED_RULE_DEFINITION_BY_VALUE[field];
};

export const getCustomFeedRuleFieldOption = (
  field: CustomFeedQueryRuleField,
) => {
  const definition = getCustomFeedRuleDefinition(field);

  return {
    icon: getCustomFeedRuleFieldIcon(field),
    label: definition.label,
    value: field,
  };
};

export const getCustomFeedRuleOperatorOptions = (
  field: CustomFeedQueryRuleField,
) => {
  return getCustomFeedRuleDefinition(field).operators.map((operator) => ({
    label: CUSTOM_FEED_RULE_OPERATOR_LABELS[operator],
    symbol: CUSTOM_FEED_RULE_OPERATOR_SYMBOLS[operator],
    value: operator,
  }));
};

export const createDefaultCustomFeedRule = (
  overrides?: Partial<
    Pick<CustomFeedQueryRule, "field" | "id" | "operator" | "value">
  >,
): CustomFeedQueryRule => {
  const field = overrides?.field ?? "postStats.totalReactionCount";
  const definition = getCustomFeedRuleDefinition(field);

  return {
    id: overrides?.id ?? crypto.randomUUID(),
    field,
    operator: overrides?.operator ?? definition.operators[0],
    value: overrides?.value ?? definition.defaultValue,
  };
};

export const createDefaultCustomFeedRuleSection = (
  overrides?: Partial<
    Pick<CustomFeedQueryRuleSection, "id" | "joinWithPrevious" | "matchMode">
  > & {
    rules?: CustomFeedQueryRule[];
  },
): CustomFeedQueryRuleSection => {
  return {
    id: overrides?.id ?? crypto.randomUUID(),
    joinWithPrevious: overrides?.joinWithPrevious,
    matchMode: overrides?.matchMode ?? "and",
    rules: overrides?.rules?.length
      ? overrides.rules
      : [createDefaultCustomFeedRule()],
  };
};

const humanizeWords = (value: string) => {
  return value
    .split("_")
    .map((word) => {
      if (word === "NFT") {
        return "NFT";
      }

      if (word === "USD") {
        return "USD";
      }

      return word.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(" ");
};

function getCustomFeedRuleFieldIcon(field: CustomFeedQueryRuleField) {
  switch (field) {
    case "isQuotedRepost":
      return toLucideIconToken("quote");
    case "isPinned":
      return toLucideIconToken("pin");
    case "hasUnlockable":
      return toLucideIconToken("lock-open");
    case "subscriptionAccessGroupExists":
      return toLucideIconToken("lock");
    case "upvoteCappedWealthUsdCents":
    case "postStats.upvoteWealthTotalUsdCents":
    case "postStats.upvoteWealthTotalUsdCentsCapped":
      return toLucideIconToken("arrow-up");
    case "totalDiamondValueUsdCents":
    case "postStats.diamondsLevelSum":
    case "postStats.diamondsTotalCount":
    case "postStats.diamondsTotalUsdCents":
      return "💎";
    case "posterTotalBalanceUsdCents":
      return toLucideIconToken("user");
    case "commentDepth":
      return toLucideIconToken("git-branch");
    case "postStats.totalReactionCount":
      return "✨";
    case "postStats.positiveReactionCount":
      return "🙂";
    case "postStats.negativeReactionCount":
      return "🙁";
    case "postStats.likeReactionCount":
      return AssociationReactionValueEmojiMap.LIKE;
    case "postStats.dislikeReactionCount":
      return AssociationReactionValueEmojiMap.DISLIKE;
    case "postStats.loveReactionCount":
      return AssociationReactionValueEmojiMap.LOVE;
    case "postStats.laughReactionCount":
      return AssociationReactionValueEmojiMap.LAUGH;
    case "postStats.astonishedReactionCount":
      return AssociationReactionValueEmojiMap.ASTONISHED;
    case "postStats.sadReactionCount":
      return AssociationReactionValueEmojiMap.SAD;
    case "postStats.angryReactionCount":
      return AssociationReactionValueEmojiMap.ANGRY;
    case "postStats.replyCount":
      return toLucideIconToken("message-circle");
    case "postStats.viewCount":
      return toLucideIconToken("eye");
    case "postStats.whaleViewCount":
      return "🐋";
    case "postStats.quoteCount":
      return toLucideIconToken("quote");
    case "postStats.repostCount":
      return toLucideIconToken("repeat-2");
    case "postStats.lastDiamondUpdateBlockHeight":
    case "postStats.lastReactionUpdateBlockHeight":
      return toLucideIconToken("hash");
  }
}

const isPostsOrderByValue = (value: unknown): value is PostsOrderBy => {
  return Object.values(PostsOrderBy).includes(value as PostsOrderBy);
};

const isPostStatsOrderByValue = (value: unknown): value is PostStatsOrderBy => {
  return Object.values(PostStatsOrderBy).includes(value as PostStatsOrderBy);
};

const getCustomFeedSortDirection = (
  value: CustomFeedQuerySortValue,
): CustomFeedSortDirection | undefined => {
  if (String(value).endsWith("_ASC")) {
    return "asc";
  }

  if (String(value).endsWith("_DESC")) {
    return "desc";
  }

  return undefined;
};

const getCustomFeedSortBaseLabel = (value: CustomFeedQuerySortValue) => {
  if (isPostsOrderByValue(value)) {
    const specialLabel = POSTS_ORDER_BY_SPECIAL_LABELS[value];

    if (specialLabel) {
      return specialLabel;
    }

    const match = value.match(/^(.*)_(ASC|DESC)$/);

    if (!match) {
      return humanizeWords(value);
    }

    return humanizeWords(match[1]);
  }

  const specialLabel = POST_STATS_ORDER_BY_SPECIAL_LABELS[value];

  if (specialLabel) {
    return specialLabel;
  }

  const match = value.match(/^(.*)_(ASC|DESC)$/);

  if (!match) {
    return humanizeWords(value);
  }

  return humanizeWords(match[1]);
};

const getCustomFeedSortIcon = (value: CustomFeedQuerySortValue) => {
  if (isPostsOrderByValue(value)) {
    const rawField = String(value).replace(/_(ASC|DESC)$/, "");
    const loweredField = rawField.toLowerCase();

    if (loweredField.includes("timestamp")) return toLucideIconToken("clock-3");
    if (loweredField.includes("upvote")) return toLucideIconToken("arrow-up");
    if (loweredField.includes("diamond")) return "💎";
    if (loweredField.includes("comment_depth")) {
      return toLucideIconToken("git-branch");
    }
    if (loweredField.includes("nft")) return toLucideIconToken("image");
    if (loweredField.includes("image")) return toLucideIconToken("image");
    if (loweredField.includes("video")) return toLucideIconToken("video");
    if (loweredField.includes("poster")) return toLucideIconToken("user");
    if (loweredField.includes("quoted_repost")) {
      return toLucideIconToken("quote");
    }
    if (loweredField.includes("unlockable")) {
      return toLucideIconToken("lock-open");
    }
    if (loweredField.includes("pinned")) return toLucideIconToken("pin");
    if (loweredField.includes("subscription")) {
      return toLucideIconToken("lock");
    }
    if (loweredField.includes("post_hash")) return toLucideIconToken("hash");
    if (loweredField.includes("body")) return toLucideIconToken("text");

    return toLucideIconToken("arrow-up-down");
  }

  switch (value) {
    case PostStatsOrderBy.LikeReactionCountAsc:
    case PostStatsOrderBy.LikeReactionCountDesc:
      return AssociationReactionValueEmojiMap.LIKE;
    case PostStatsOrderBy.DislikeReactionCountAsc:
    case PostStatsOrderBy.DislikeReactionCountDesc:
      return AssociationReactionValueEmojiMap.DISLIKE;
    case PostStatsOrderBy.LoveReactionCountAsc:
    case PostStatsOrderBy.LoveReactionCountDesc:
      return AssociationReactionValueEmojiMap.LOVE;
    case PostStatsOrderBy.LaughReactionCountAsc:
    case PostStatsOrderBy.LaughReactionCountDesc:
      return AssociationReactionValueEmojiMap.LAUGH;
    case PostStatsOrderBy.AstonishedReactionCountAsc:
    case PostStatsOrderBy.AstonishedReactionCountDesc:
      return AssociationReactionValueEmojiMap.ASTONISHED;
    case PostStatsOrderBy.SadReactionCountAsc:
    case PostStatsOrderBy.SadReactionCountDesc:
      return AssociationReactionValueEmojiMap.SAD;
    case PostStatsOrderBy.AngryReactionCountAsc:
    case PostStatsOrderBy.AngryReactionCountDesc:
      return AssociationReactionValueEmojiMap.ANGRY;
    case PostStatsOrderBy.TotalReactionCountAsc:
    case PostStatsOrderBy.TotalReactionCountDesc:
      return "✨";
    case PostStatsOrderBy.PositiveReactionCountAsc:
    case PostStatsOrderBy.PositiveReactionCountDesc:
      return "🙂";
    case PostStatsOrderBy.NegativeReactionCountAsc:
    case PostStatsOrderBy.NegativeReactionCountDesc:
      return "🙁";
    case PostStatsOrderBy.ReplyCountAsc:
    case PostStatsOrderBy.ReplyCountDesc:
      return toLucideIconToken("message-circle");
    case PostStatsOrderBy.ViewCountAsc:
    case PostStatsOrderBy.ViewCountDesc:
      return toLucideIconToken("eye");
    case PostStatsOrderBy.WhaleViewCountAsc:
    case PostStatsOrderBy.WhaleViewCountDesc:
      return "🐋";
    case PostStatsOrderBy.QuoteCountAsc:
    case PostStatsOrderBy.QuoteCountDesc:
      return toLucideIconToken("quote");
    case PostStatsOrderBy.RepostCountAsc:
    case PostStatsOrderBy.RepostCountDesc:
      return toLucideIconToken("repeat-2");
    case PostStatsOrderBy.DiamondsLevelSumAsc:
    case PostStatsOrderBy.DiamondsLevelSumDesc:
    case PostStatsOrderBy.DiamondsTotalCountAsc:
    case PostStatsOrderBy.DiamondsTotalCountDesc:
    case PostStatsOrderBy.DiamondsTotalUsdCentsAsc:
    case PostStatsOrderBy.DiamondsTotalUsdCentsDesc:
      return "💎";
    case PostStatsOrderBy.UpvoteWealthTotalUsdCentsAsc:
    case PostStatsOrderBy.UpvoteWealthTotalUsdCentsDesc:
    case PostStatsOrderBy.UpvoteWealthTotalUsdCentsCappedAsc:
    case PostStatsOrderBy.UpvoteWealthTotalUsdCentsCappedDesc:
      return toLucideIconToken("arrow-up");
    case PostStatsOrderBy.LastDiamondUpdateBlockHeightAsc:
    case PostStatsOrderBy.LastDiamondUpdateBlockHeightDesc:
    case PostStatsOrderBy.LastReactionUpdateBlockHeightAsc:
    case PostStatsOrderBy.LastReactionUpdateBlockHeightDesc:
      return toLucideIconToken("hash");
    default:
      return toLucideIconToken("arrow-up-down");
  }
};

const getPostsOrderByLabel = (value: PostsOrderBy) => {
  const baseLabel = getCustomFeedSortBaseLabel(value);
  const direction = getCustomFeedSortDirection(value);

  return direction
    ? `${baseLabel} · ${direction === "asc" ? "Asc" : "Desc"}`
    : baseLabel;
};

const getPostStatsOrderByLabel = (value: PostStatsOrderBy) => {
  const baseLabel = getCustomFeedSortBaseLabel(value);
  const direction = getCustomFeedSortDirection(value);

  return direction
    ? `${baseLabel} · ${direction === "asc" ? "Asc" : "Desc"}`
    : baseLabel;
};

const POSTS_ORDER_BY_PRIORITY: PostsOrderBy[] = [
  PostsOrderBy.TimestampDesc,
  PostsOrderBy.TimestampAsc,
  PostsOrderBy.UpvoteCappedWealthUsdCentsDesc,
  PostsOrderBy.UpvoteCappedWealthUsdCentsAsc,
  PostsOrderBy.UpvoteWealthUsdCentsDesc,
  PostsOrderBy.UpvoteWealthUsdCentsAsc,
  PostsOrderBy.TotalDiamondValueUsdCentsDesc,
  PostsOrderBy.TotalDiamondValueUsdCentsAsc,
  PostsOrderBy.DiamondCountDesc,
  PostsOrderBy.DiamondCountAsc,
  PostsOrderBy.DiamondLevelSumDesc,
  PostsOrderBy.DiamondLevelSumAsc,
  PostsOrderBy.PosterTotalBalanceUsdCentsDesc,
  PostsOrderBy.PosterTotalBalanceUsdCentsAsc,
  PostsOrderBy.PosterDesoBalanceUsdCentsDesc,
  PostsOrderBy.PosterDesoBalanceUsdCentsAsc,
  PostsOrderBy.PosterFocusTokenBalanceUsdCentsDesc,
  PostsOrderBy.PosterFocusTokenBalanceUsdCentsAsc,
  PostsOrderBy.PosterUsdBalanceUsdCentsDesc,
  PostsOrderBy.PosterUsdBalanceUsdCentsAsc,
  PostsOrderBy.CommentDepthDesc,
  PostsOrderBy.CommentDepthAsc,
  PostsOrderBy.IsQuotedRepostDesc,
  PostsOrderBy.IsQuotedRepostAsc,
  PostsOrderBy.IsNftDesc,
  PostsOrderBy.IsNftAsc,
  PostsOrderBy.HasUnlockableDesc,
  PostsOrderBy.HasUnlockableAsc,
  PostsOrderBy.BodyAsc,
  PostsOrderBy.BodyDesc,
  PostsOrderBy.PosterPublicKeyAsc,
  PostsOrderBy.PosterPublicKeyDesc,
  PostsOrderBy.PostHashAsc,
  PostsOrderBy.PostHashDesc,
  PostsOrderBy.ParentPostHashAsc,
  PostsOrderBy.ParentPostHashDesc,
  PostsOrderBy.RootPostHashAsc,
  PostsOrderBy.RootPostHashDesc,
  PostsOrderBy.RepostedPostHashAsc,
  PostsOrderBy.RepostedPostHashDesc,
  PostsOrderBy.ImageUrlsAsc,
  PostsOrderBy.ImageUrlsDesc,
  PostsOrderBy.VideoUrlsAsc,
  PostsOrderBy.VideoUrlsDesc,
  PostsOrderBy.IsHiddenAsc,
  PostsOrderBy.IsHiddenDesc,
  PostsOrderBy.IsPinnedAsc,
  PostsOrderBy.IsPinnedDesc,
  PostsOrderBy.NumNftCopiesAsc,
  PostsOrderBy.NumNftCopiesDesc,
  PostsOrderBy.NumNftCopiesForSaleAsc,
  PostsOrderBy.NumNftCopiesForSaleDesc,
  PostsOrderBy.NumNftCopiesBurnedAsc,
  PostsOrderBy.NumNftCopiesBurnedDesc,
  PostsOrderBy.NftRoyaltyToCreatorBasisPointsAsc,
  PostsOrderBy.NftRoyaltyToCreatorBasisPointsDesc,
  PostsOrderBy.NftRoyaltyToCoinBasisPointsAsc,
  PostsOrderBy.NftRoyaltyToCoinBasisPointsDesc,
  PostsOrderBy.AdditionalNftRoyaltiesToCoinsBasisPointsAsc,
  PostsOrderBy.AdditionalNftRoyaltiesToCoinsBasisPointsDesc,
  PostsOrderBy.AdditionalNftRoyaltiesToCreatorsBasisPointsAsc,
  PostsOrderBy.AdditionalNftRoyaltiesToCreatorsBasisPointsDesc,
  PostsOrderBy.SubscriptionAccessGroupOwnerPublicKeyAsc,
  PostsOrderBy.SubscriptionAccessGroupOwnerPublicKeyDesc,
  PostsOrderBy.SubscriptionAccessGroupKeyNameAsc,
  PostsOrderBy.SubscriptionAccessGroupKeyNameDesc,
  PostsOrderBy.BadgerKeyAsc,
  PostsOrderBy.BadgerKeyDesc,
  PostsOrderBy.IsFrozenAsc,
  PostsOrderBy.IsFrozenDesc,
  PostsOrderBy.ExtraDataAsc,
  PostsOrderBy.ExtraDataDesc,
  PostsOrderBy.Natural,
];

const POST_STATS_ORDER_BY_PRIORITY: PostStatsOrderBy[] = [
  PostStatsOrderBy.LikeReactionCountDesc,
  PostStatsOrderBy.LikeReactionCountAsc,
  PostStatsOrderBy.LoveReactionCountDesc,
  PostStatsOrderBy.LoveReactionCountAsc,
  PostStatsOrderBy.LaughReactionCountDesc,
  PostStatsOrderBy.LaughReactionCountAsc,
  PostStatsOrderBy.DislikeReactionCountDesc,
  PostStatsOrderBy.DislikeReactionCountAsc,
  PostStatsOrderBy.AngryReactionCountDesc,
  PostStatsOrderBy.AngryReactionCountAsc,
  PostStatsOrderBy.AstonishedReactionCountDesc,
  PostStatsOrderBy.AstonishedReactionCountAsc,
  PostStatsOrderBy.SadReactionCountDesc,
  PostStatsOrderBy.SadReactionCountAsc,
  PostStatsOrderBy.TotalReactionCountDesc,
  PostStatsOrderBy.TotalReactionCountAsc,
  PostStatsOrderBy.PositiveReactionCountDesc,
  PostStatsOrderBy.PositiveReactionCountAsc,
  PostStatsOrderBy.NegativeReactionCountDesc,
  PostStatsOrderBy.NegativeReactionCountAsc,
  PostStatsOrderBy.ReplyCountDesc,
  PostStatsOrderBy.ReplyCountAsc,
  PostStatsOrderBy.ViewCountDesc,
  PostStatsOrderBy.ViewCountAsc,
  PostStatsOrderBy.WhaleViewCountDesc,
  PostStatsOrderBy.WhaleViewCountAsc,
  PostStatsOrderBy.QuoteCountDesc,
  PostStatsOrderBy.QuoteCountAsc,
  PostStatsOrderBy.RepostCountDesc,
  PostStatsOrderBy.RepostCountAsc,
  PostStatsOrderBy.DiamondsLevelSumDesc,
  PostStatsOrderBy.DiamondsLevelSumAsc,
  PostStatsOrderBy.DiamondsTotalCountDesc,
  PostStatsOrderBy.DiamondsTotalCountAsc,
  PostStatsOrderBy.DiamondsTotalUsdCentsDesc,
  PostStatsOrderBy.DiamondsTotalUsdCentsAsc,
  PostStatsOrderBy.UpvoteWealthTotalUsdCentsDesc,
  PostStatsOrderBy.UpvoteWealthTotalUsdCentsAsc,
  PostStatsOrderBy.UpvoteWealthTotalUsdCentsCappedDesc,
  PostStatsOrderBy.UpvoteWealthTotalUsdCentsCappedAsc,
  PostStatsOrderBy.LastDiamondUpdateBlockHeightDesc,
  PostStatsOrderBy.LastDiamondUpdateBlockHeightAsc,
  PostStatsOrderBy.LastReactionUpdateBlockHeightDesc,
  PostStatsOrderBy.LastReactionUpdateBlockHeightAsc,
];

const uniquePostsOrderByOptions = Array.from(
  new Set([...POSTS_ORDER_BY_PRIORITY, ...Object.values(PostsOrderBy)]),
);
const uniquePostStatsOrderByOptions = Array.from(
  new Set(POST_STATS_ORDER_BY_PRIORITY),
);

CUSTOM_FEED_SORT_OPTIONS.push(
  ...uniquePostsOrderByOptions.map((value) => ({
    direction: getCustomFeedSortDirection(value),
    icon: getCustomFeedSortIcon(value),
    label: getPostsOrderByLabel(value),
    value,
    source: "posts" as const,
  })),
  ...uniquePostStatsOrderByOptions.map((value) => ({
    direction: getCustomFeedSortDirection(value),
    icon: getCustomFeedSortIcon(value),
    label: getPostStatsOrderByLabel(value),
    value,
    source: "post-stats" as const,
  })),
);

CUSTOM_FEED_EDITOR_SORT_OPTIONS.push(
  ...CUSTOM_FEED_SORT_OPTIONS.filter((option) => {
    return !CUSTOM_FEED_EDITOR_HIDDEN_SORTS.has(option.value);
  }),
);

export const getCustomFeedSortSource = (
  value: CustomFeedQuerySortValue,
): CustomFeedQuerySource => {
  return isPostStatsOrderByValue(value) ? "post-stats" : "posts";
};

const isCustomFeedQuerySortValue = (
  value: unknown,
): value is CustomFeedQuerySortValue => {
  return isPostsOrderByValue(value) || isPostStatsOrderByValue(value);
};

const normalizeCustomFeedSort = (value: unknown): CustomFeedQuerySort => {
  if (Array.isArray(value)) {
    const sortValues = value.filter(isCustomFeedQuerySortValue);
    const uniqueSortValues = Array.from(new Set(sortValues));

    if (uniqueSortValues.length > 0) {
      const source = getCustomFeedSortSource(uniqueSortValues[0]);
      return uniqueSortValues.filter(
        (sortValue) => getCustomFeedSortSource(sortValue) === source,
      );
    }
  }

  if (isCustomFeedQuerySortValue(value)) {
    return [value];
  }

  if (value === "new") {
    return [PostsOrderBy.TimestampDesc];
  }

  if (value === "trending") {
    return [
      PostsOrderBy.UpvoteWealthUsdCentsDesc,
      PostsOrderBy.TotalDiamondValueUsdCentsDesc,
      PostsOrderBy.TimestampDesc,
    ];
  }

  return DEFAULT_CUSTOM_FEED_QUERY_CONFIG.sortBy;
};

const isCustomFeedRelativeTimeframeUnit = (
  value: unknown,
): value is CustomFeedRelativeTimeframeUnit => {
  return CUSTOM_FEED_RELATIVE_TIMEFRAME_UNIT_OPTIONS.some(
    (option) => option.value === value,
  );
};

const isCustomFeedQueryLogicalOperator = (
  value: unknown,
): value is CustomFeedQueryLogicalOperator => {
  return value === "and" || value === "or";
};

const isCustomFeedQueryRuleOperator = (
  value: unknown,
): value is CustomFeedQueryRuleOperator => {
  return (
    typeof value === "string" &&
    Object.hasOwn(CUSTOM_FEED_RULE_OPERATOR_LABELS, value)
  );
};

const normalizeCustomFeedRuleValue = (
  definition: CustomFeedRuleDefinition,
  value: unknown,
) => {
  if (definition.valueType === "boolean") {
    if (typeof value === "boolean") {
      return value;
    }

    if (value === "true") {
      return true;
    }

    if (value === "false") {
      return false;
    }

    return definition.defaultValue;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return definition.defaultValue;
};

const normalizeCustomFeedRule = (
  value: unknown,
  index: number,
): CustomFeedQueryRule | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const field =
    typeof value.field === "string" &&
    Object.hasOwn(CUSTOM_FEED_RULE_DEFINITION_BY_VALUE, value.field)
      ? (value.field as CustomFeedQueryRuleField)
      : null;

  if (!field) {
    return undefined;
  }

  const definition = getCustomFeedRuleDefinition(field);
  const operator =
    isCustomFeedQueryRuleOperator(value.operator) &&
    definition.operators.includes(value.operator)
      ? value.operator
      : definition.operators[0];

  return createDefaultCustomFeedRule({
    field,
    id:
      typeof value.id === "string" && value.id.length > 0
        ? value.id
        : `advanced-rule-${index}`,
    operator,
    value: normalizeCustomFeedRuleValue(definition, value.value),
  });
};

const normalizeCustomFeedRuleSections = (
  value: unknown,
  legacyMatchMode?: unknown,
  legacyRules?: unknown,
): CustomFeedQueryRuleSection[] => {
  if (Array.isArray(value)) {
    const sections = value.flatMap((section, sectionIndex) => {
      if (!isRecord(section)) {
        return [];
      }

      const normalizedRulesSource = Array.isArray(section.rules)
        ? section.rules
        : [];
      const rules = normalizedRulesSource.flatMap((rule, ruleIndex) => {
        const normalizedRule = normalizeCustomFeedRule(
          rule,
          sectionIndex * 100 + ruleIndex,
        );
        return normalizedRule ? [normalizedRule] : [];
      });

      if (rules.length === 0) {
        return [];
      }

      return [
        createDefaultCustomFeedRuleSection({
          id:
            typeof section.id === "string" && section.id.length > 0
              ? section.id
              : `advanced-section-${sectionIndex}`,
          joinWithPrevious:
            sectionIndex === 0
              ? undefined
              : isCustomFeedQueryLogicalOperator(section.joinWithPrevious)
                ? section.joinWithPrevious
                : "or",
          matchMode: isCustomFeedQueryLogicalOperator(section.matchMode)
            ? section.matchMode
            : "and",
          rules,
        }),
      ];
    });

    return sections.map<CustomFeedQueryRuleSection>((section, index) => ({
      ...section,
      joinWithPrevious:
        index === 0
          ? undefined
          : isCustomFeedQueryLogicalOperator(section.joinWithPrevious)
            ? section.joinWithPrevious
            : "or",
    }));
  }

  if (Array.isArray(legacyRules)) {
    const rules = legacyRules.flatMap((rule, index) => {
      const normalizedRule = normalizeCustomFeedRule(rule, index);
      return normalizedRule ? [normalizedRule] : [];
    });

    if (rules.length > 0) {
      return [
        createDefaultCustomFeedRuleSection({
          matchMode:
            legacyMatchMode === "any"
              ? "or"
              : legacyMatchMode === "all"
                ? "and"
                : "and",
          rules,
        }),
      ];
    }
  }

  return DEFAULT_CUSTOM_FEED_QUERY_CONFIG.advancedSections;
};

const isValidDateString = (value: unknown) => {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    !Number.isNaN(new Date(value).getTime())
  );
};

const normalizeCustomFeedAuthor = (
  value: unknown,
): CustomFeedQueryAuthor | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const publicKey =
    typeof value.publicKey === "string" && isDesoPublicKey(value.publicKey)
      ? value.publicKey
      : null;

  if (!publicKey) {
    return undefined;
  }

  const username =
    typeof value.username === "string" &&
    /^[a-zA-Z0-9_]{1,26}$/.test(value.username)
      ? value.username
      : undefined;

  return {
    publicKey,
    username,
  };
};

const normalizeCustomFeedTimeframe = (value: unknown): CustomFeedTimeframe => {
  if (isRecord(value)) {
    if (value.mode === "relative") {
      return {
        mode: "relative",
        amount: clampRelativeTimeframeAmount(value.amount),
        unit: isCustomFeedRelativeTimeframeUnit(value.unit)
          ? value.unit
          : "days",
      };
    }

    if (value.mode === "range") {
      const from = isValidDateString(value.from)
        ? new Date(value.from as string).toISOString()
        : "";
      const to = isValidDateString(value.to)
        ? new Date(value.to as string).toISOString()
        : "";

      if (from && to && from > to) {
        return {
          mode: "range",
          from: to,
          to: from,
        };
      }

      return {
        mode: "range",
        from,
        to,
      };
    }
  }

  return {
    mode: "anytime",
  };
};

const getLegacyTimeframe = (
  value: Record<string, unknown>,
): CustomFeedTimeframe => {
  const recentDays =
    typeof value.recentDays === "number" && Number.isFinite(value.recentDays)
      ? Math.max(0, Math.round(value.recentDays))
      : 0;

  if (recentDays > 0) {
    return {
      mode: "relative",
      amount: recentDays,
      unit: "days",
    };
  }

  return {
    mode: "anytime",
  };
};

const subtractRelativeTime = ({
  amount,
  now = new Date(),
  unit,
}: {
  amount: number;
  now?: Date;
  unit: CustomFeedRelativeTimeframeUnit;
}) => {
  const nextDate = new Date(now);

  switch (unit) {
    case "minutes":
      nextDate.setMinutes(nextDate.getMinutes() - amount);
      break;
    case "hours":
      nextDate.setHours(nextDate.getHours() - amount);
      break;
    case "days":
      nextDate.setDate(nextDate.getDate() - amount);
      break;
    case "weeks":
      nextDate.setDate(nextDate.getDate() - amount * 7);
      break;
    case "months":
      nextDate.setMonth(nextDate.getMonth() - amount);
      break;
    case "years":
      nextDate.setFullYear(nextDate.getFullYear() - amount);
      break;
  }

  return nextDate.toISOString();
};

const buildCustomFeedRuleFilter = (
  rule: CustomFeedQueryRule,
): PostFilter | undefined => {
  const definition = getCustomFeedRuleDefinition(rule.field);

  if (definition.target === "root-exists") {
    if (rule.operator === "equalTo") {
      return {
        [definition.filterField]: rule.value,
      } as PostFilter;
    }

    if (rule.operator === "notEqualTo") {
      return {
        not: {
          [definition.filterField]: rule.value,
        } as PostFilter,
      };
    }

    return undefined;
  }

  if (definition.target === "root-filter") {
    return {
      [definition.filterField]: {
        [rule.operator]: rule.value,
      },
    } as PostFilter;
  }

  return {
    postStats: {
      [definition.filterField]: {
        [rule.operator]: rule.value,
      },
    },
  } as PostFilter;
};

const isConditionEligibleCustomFeedRule = (
  rule: CustomFeedQueryRule,
  sectionMatchMode: CustomFeedQueryLogicalOperator,
  topLevelCanLift: boolean,
) => {
  const definition = getCustomFeedRuleDefinition(rule.field);

  return (
    topLevelCanLift &&
    sectionMatchMode === "and" &&
    rule.operator === "equalTo" &&
    !!definition.postConditionField &&
    definition.target === "root-filter"
  );
};

const buildSectionFilter = (
  section: CustomFeedQueryRuleSection,
  topLevelCanLiftConditions: boolean,
) => {
  const sectionFilters = section.rules.flatMap((rule) => {
    if (
      isConditionEligibleCustomFeedRule(
        rule,
        section.matchMode,
        topLevelCanLiftConditions,
      )
    ) {
      return [];
    }

    const filter = buildCustomFeedRuleFilter(rule);
    return filter ? [filter] : [];
  });

  if (sectionFilters.length === 0) {
    return undefined;
  }

  if (section.matchMode === "and") {
    return combinePostFilters(...sectionFilters);
  }

  return sectionFilters.length === 1
    ? sectionFilters[0]
    : {
        or: sectionFilters,
      };
};

const combineSectionFilters = (sections: CustomFeedQueryRuleSection[]) => {
  const sectionEntries = sections.flatMap((section) => {
    const filter = buildSectionFilter(section, false);
    return filter
      ? [{ joinWithPrevious: section.joinWithPrevious, filter }]
      : [];
  });

  if (sectionEntries.length === 0) {
    return undefined;
  }

  let combinedFilter = sectionEntries[0].filter;

  for (let index = 1; index < sectionEntries.length; index += 1) {
    const sectionEntry = sectionEntries[index];

    combinedFilter =
      sectionEntry.joinWithPrevious === "or"
        ? {
            or: [combinedFilter, sectionEntry.filter],
          }
        : (combinePostFilters(combinedFilter, sectionEntry.filter) ??
          combinedFilter);
  }

  return combinedFilter;
};

export const getCustomFeedSortLabel = (sortBy: CustomFeedQuerySort) => {
  const normalizedSort = normalizeCustomFeedSort(sortBy);

  return normalizedSort
    .map((sortValue) => {
      return (
        CUSTOM_FEED_SORT_OPTIONS.find((option) => option.value === sortValue)
          ?.label ??
        (isPostsOrderByValue(sortValue)
          ? getPostsOrderByLabel(sortValue)
          : getPostStatsOrderByLabel(sortValue))
      );
    })
    .join(" → ");
};

export const getCustomFeedTimeframeLabel = (timeframe: CustomFeedTimeframe) => {
  if (timeframe.mode === "anytime") {
    return "Any time";
  }

  if (timeframe.mode === "relative") {
    const label =
      CUSTOM_FEED_RELATIVE_TIMEFRAME_UNIT_OPTIONS.find(
        (option) => option.value === timeframe.unit,
      )?.label ?? "Days";

    return `Last ${timeframe.amount} ${
      timeframe.amount === 1 ? label.slice(0, -1) : label
    }`;
  }

  if (timeframe.from && timeframe.to) {
    return "Custom range";
  }

  if (timeframe.from) {
    return "From date";
  }

  if (timeframe.to) {
    return "Until date";
  }

  return "Any time";
};

export const getCustomFeedAdvancedRulesLabel = (
  sections: CustomFeedQueryRuleSection[],
) => {
  if (sections.length === 0) {
    return null;
  }

  const ruleCount = sections.reduce(
    (count, section) => count + section.rules.length,
    0,
  );

  return `${sections.length} group${sections.length === 1 ? "" : "s"} • ${ruleCount} rule${
    ruleCount === 1 ? "" : "s"
  }`;
};

export const normalizeCustomFeedQueryConfig = (
  value?: unknown,
): CustomFeedQueryConfig => {
  if (!isRecord(value)) {
    return DEFAULT_CUSTOM_FEED_QUERY_CONFIG;
  }

  return {
    author:
      normalizeCustomFeedAuthor(value.author) ??
      (typeof value.authorPublicKey === "string"
        ? normalizeCustomFeedAuthor({
            publicKey: value.authorPublicKey,
            username: value.authorUsername,
          })
        : undefined),
    keyword: sanitizeCustomFeedKeyword(
      typeof value.keyword === "string" ? value.keyword : value.queryText,
    ),
    sortBy: normalizeCustomFeedSort(value.sortBy),
    timeframe: isRecord(value.timeframe)
      ? normalizeCustomFeedTimeframe(value.timeframe)
      : getLegacyTimeframe(value),
    includeReposts:
      typeof value.includeReposts === "boolean"
        ? value.includeReposts
        : DEFAULT_CUSTOM_FEED_QUERY_CONFIG.includeReposts,
    includeQuotes:
      typeof value.includeQuotes === "boolean"
        ? value.includeQuotes
        : DEFAULT_CUSTOM_FEED_QUERY_CONFIG.includeQuotes,
    hasImages:
      typeof value.hasImages === "boolean"
        ? value.hasImages
        : DEFAULT_CUSTOM_FEED_QUERY_CONFIG.hasImages,
    hasVideos:
      typeof value.hasVideos === "boolean"
        ? value.hasVideos
        : DEFAULT_CUSTOM_FEED_QUERY_CONFIG.hasVideos,
    hasAudio:
      typeof value.hasAudio === "boolean"
        ? value.hasAudio
        : DEFAULT_CUSTOM_FEED_QUERY_CONFIG.hasAudio,
    isGated:
      typeof value.isGated === "boolean"
        ? value.isGated
        : DEFAULT_CUSTOM_FEED_QUERY_CONFIG.isGated,
    isNft:
      typeof value.isNft === "boolean"
        ? value.isNft
        : DEFAULT_CUSTOM_FEED_QUERY_CONFIG.isNft,
    advancedSections: normalizeCustomFeedRuleSections(
      value.advancedSections,
      value.advancedRuleMatchMode,
      value.advancedRules,
    ),
  };
};

export const getCustomFeedQuerySource = (
  config: CustomFeedQueryConfig,
): CustomFeedQuerySource => {
  const normalizedConfig = normalizeCustomFeedQueryConfig(config);
  return getCustomFeedSortSource(normalizedConfig.sortBy[0]);
};

export const getCustomFeedQueryConfigFromPreset = (
  presetId: FeedQueryPresetId,
): CustomFeedQueryConfig => {
  switch (presetId) {
    case "trending":
      return {
        ...DEFAULT_CUSTOM_FEED_QUERY_CONFIG,
        sortBy: [
          PostsOrderBy.UpvoteWealthUsdCentsDesc,
          PostsOrderBy.TotalDiamondValueUsdCentsDesc,
          PostsOrderBy.TimestampDesc,
        ],
        timeframe: {
          mode: "relative",
          amount: ONE_WEEK_IN_MS / ONE_DAY_IN_MS,
          unit: "days",
        },
      };
    case "images":
      return {
        ...DEFAULT_CUSTOM_FEED_QUERY_CONFIG,
        hasImages: true,
      };
    case "videos":
      return {
        ...DEFAULT_CUSTOM_FEED_QUERY_CONFIG,
        hasVideos: true,
      };
    case "audio":
      return {
        ...DEFAULT_CUSTOM_FEED_QUERY_CONFIG,
        hasAudio: true,
      };
    case "nfts":
      return {
        ...DEFAULT_CUSTOM_FEED_QUERY_CONFIG,
        isNft: true,
      };
    case "new":
    default:
      return DEFAULT_CUSTOM_FEED_QUERY_CONFIG;
  }
};

export const getMatchingFeedQueryPresetId = (
  value: CustomFeedQueryConfig,
): FeedQueryPresetId | null => {
  const normalizedValue = normalizeCustomFeedQueryConfig(value);

  for (const presetId of FEED_QUERY_PRESET_ORDER) {
    const presetConfig = getCustomFeedQueryConfigFromPreset(presetId);

    if (JSON.stringify(normalizedValue) === JSON.stringify(presetConfig)) {
      return presetId;
    }
  }

  return null;
};

export const buildCustomFeedFilter = (
  config: CustomFeedQueryConfig,
  { now }: { now?: Date } = {},
): PostFilter | undefined => {
  const normalizedConfig = normalizeCustomFeedQueryConfig(config);
  const mediaFilters = [
    normalizedConfig.hasImages ? IMAGES_FILTER : undefined,
    normalizedConfig.hasVideos ? VIDEOS_FILTER : undefined,
    normalizedConfig.hasAudio ? AUDIO_FILTER : undefined,
  ].filter(Boolean) as PostFilter[];
  const topLevelCanLiftConditions =
    normalizedConfig.advancedSections.length === 1 &&
    normalizedConfig.advancedSections.every(
      (section) => section.matchMode === "and",
    );
  const advancedSectionsFilter = topLevelCanLiftConditions
    ? normalizedConfig.advancedSections
        .flatMap((section) => {
          const filter = buildSectionFilter(section, true);
          return filter ? [filter] : [];
        })
        .reduce<PostFilter | undefined>(
          (combined, sectionFilter) =>
            combinePostFilters(combined, sectionFilter),
          undefined,
        )
    : combineSectionFilters(normalizedConfig.advancedSections);

  return combinePostFilters(
    buildCustomFeedKeywordPostFilter(normalizedConfig.keyword),
    normalizedConfig.timeframe.mode === "relative"
      ? {
          timestamp: {
            greaterThanOrEqualTo: subtractRelativeTime({
              amount: normalizedConfig.timeframe.amount,
              now,
              unit: normalizedConfig.timeframe.unit,
            }),
          },
        }
      : normalizedConfig.timeframe.mode === "range"
        ? hasNonEmptyFilterValue({
            ...(normalizedConfig.timeframe.from
              ? {
                  greaterThanOrEqualTo: normalizedConfig.timeframe.from,
                }
              : {}),
            ...(normalizedConfig.timeframe.to
              ? {
                  lessThanOrEqualTo: normalizedConfig.timeframe.to,
                }
              : {}),
          })
          ? {
              timestamp: {
                ...(normalizedConfig.timeframe.from
                  ? {
                      greaterThanOrEqualTo: normalizedConfig.timeframe.from,
                    }
                  : {}),
                ...(normalizedConfig.timeframe.to
                  ? {
                      lessThanOrEqualTo: normalizedConfig.timeframe.to,
                    }
                  : {}),
              },
            }
          : undefined
        : undefined,
    mediaFilters.length === 0
      ? undefined
      : mediaFilters.length === 1
        ? mediaFilters[0]
        : {
            or: mediaFilters,
          },
    getCustomFeedRepostFilter(normalizedConfig),
    normalizedConfig.isGated ? GATED_FILTER : undefined,
    advancedSectionsFilter,
  );
};

export const buildCustomFeedCondition = (
  config: CustomFeedQueryConfig,
): PostCondition | undefined => {
  const normalizedConfig = normalizeCustomFeedQueryConfig(config);
  const condition: PostCondition = {};
  const topLevelCanLiftConditions =
    normalizedConfig.advancedSections.length === 1 &&
    normalizedConfig.advancedSections.every(
      (section) => section.matchMode === "and",
    );

  if (normalizedConfig.author?.publicKey) {
    condition.posterPublicKey = normalizedConfig.author.publicKey;
  }

  if (normalizedConfig.isNft) {
    condition.isNft = true;
  }

  if (topLevelCanLiftConditions) {
    for (const section of normalizedConfig.advancedSections) {
      for (const rule of section.rules) {
        if (!isConditionEligibleCustomFeedRule(rule, section.matchMode, true)) {
          continue;
        }

        const definition = getCustomFeedRuleDefinition(rule.field);

        if (!definition.postConditionField) {
          continue;
        }

        condition[definition.postConditionField] = rule.value as never;
      }
    }
  }

  return Object.keys(condition).length > 0 ? condition : undefined;
};

export const getCustomFeedOrderBy = (
  config: CustomFeedQueryConfig,
): CustomFeedQuerySort => {
  return normalizeCustomFeedQueryConfig(config).sortBy;
};

const combinePostStatFilters = (
  ...filters: Array<PostStatFilter | undefined>
): PostStatFilter | undefined => {
  const validFilters = filters.filter((filter): filter is PostStatFilter =>
    hasNonEmptyFilterValue(filter),
  );

  if (validFilters.length === 0) {
    return undefined;
  }

  if (validFilters.length === 1) {
    return validFilters[0];
  }

  return {
    and: validFilters,
  };
};

const wrapPostFilterInPostStatsFilter = (
  filter: PostFilter | undefined,
): PostStatFilter | undefined => {
  return filter
    ? ({
        post: filter,
      } as PostStatFilter)
    : undefined;
};

const buildCustomFeedPostStatsRuleFilter = (
  rule: CustomFeedQueryRule,
): PostStatFilter | undefined => {
  const definition = getCustomFeedRuleDefinition(rule.field);

  if (definition.target === "root-exists") {
    if (rule.operator === "equalTo") {
      return {
        post: {
          [definition.filterField]: rule.value,
        } as PostFilter,
      };
    }

    if (rule.operator === "notEqualTo") {
      return {
        not: {
          post: {
            [definition.filterField]: rule.value,
          } as PostFilter,
        },
      };
    }

    return undefined;
  }

  if (definition.target === "root-filter") {
    return {
      post: {
        [definition.filterField]: {
          [rule.operator]: rule.value,
        },
      } as PostFilter,
    };
  }

  return {
    [definition.filterField]: {
      [rule.operator]: rule.value,
    },
  } as PostStatFilter;
};

const isConditionEligibleCustomFeedPostStatsRule = (
  rule: CustomFeedQueryRule,
  sectionMatchMode: CustomFeedQueryLogicalOperator,
  topLevelCanLift: boolean,
) => {
  const definition = getCustomFeedRuleDefinition(rule.field);

  return (
    topLevelCanLift &&
    sectionMatchMode === "and" &&
    rule.operator === "equalTo" &&
    !!definition.postStatConditionField &&
    definition.target === "post-stats"
  );
};

const buildPostStatsSectionFilter = (
  section: CustomFeedQueryRuleSection,
  topLevelCanLiftConditions: boolean,
) => {
  const sectionFilters = section.rules.flatMap((rule) => {
    if (
      isConditionEligibleCustomFeedPostStatsRule(
        rule,
        section.matchMode,
        topLevelCanLiftConditions,
      )
    ) {
      return [];
    }

    const filter = buildCustomFeedPostStatsRuleFilter(rule);
    return filter ? [filter] : [];
  });

  if (sectionFilters.length === 0) {
    return undefined;
  }

  if (section.matchMode === "and") {
    return combinePostStatFilters(...sectionFilters);
  }

  return sectionFilters.length === 1
    ? sectionFilters[0]
    : {
        or: sectionFilters,
      };
};

const combinePostStatsSectionFilters = (
  sections: CustomFeedQueryRuleSection[],
) => {
  const sectionEntries = sections.flatMap((section) => {
    const filter = buildPostStatsSectionFilter(section, false);
    return filter
      ? [{ joinWithPrevious: section.joinWithPrevious, filter }]
      : [];
  });

  if (sectionEntries.length === 0) {
    return undefined;
  }

  let combinedFilter = sectionEntries[0].filter;

  for (let index = 1; index < sectionEntries.length; index += 1) {
    const sectionEntry = sectionEntries[index];

    combinedFilter =
      sectionEntry.joinWithPrevious === "or"
        ? {
            or: [combinedFilter, sectionEntry.filter],
          }
        : (combinePostStatFilters(combinedFilter, sectionEntry.filter) ??
          combinedFilter);
  }

  return combinedFilter;
};

export const buildCustomFeedPostStatsFilter = (
  config: CustomFeedQueryConfig,
  { now }: { now?: Date } = {},
): PostStatFilter | undefined => {
  const normalizedConfig = normalizeCustomFeedQueryConfig(config);
  const mediaFilters = [
    normalizedConfig.hasImages ? IMAGES_FILTER : undefined,
    normalizedConfig.hasVideos ? VIDEOS_FILTER : undefined,
    normalizedConfig.hasAudio ? AUDIO_FILTER : undefined,
  ].filter(Boolean) as PostFilter[];
  const topLevelCanLiftConditions =
    normalizedConfig.advancedSections.length === 1 &&
    normalizedConfig.advancedSections.every(
      (section) => section.matchMode === "and",
    );
  const advancedSectionsFilter = topLevelCanLiftConditions
    ? normalizedConfig.advancedSections
        .flatMap((section) => {
          const filter = buildPostStatsSectionFilter(section, true);
          return filter ? [filter] : [];
        })
        .reduce<PostStatFilter | undefined>(
          (combined, sectionFilter) =>
            combinePostStatFilters(combined, sectionFilter),
          undefined,
        )
    : combinePostStatsSectionFilters(normalizedConfig.advancedSections);

  return combinePostStatFilters(
    normalizedConfig.author?.publicKey
      ? {
          post: {
            posterPublicKey: {
              equalTo: normalizedConfig.author.publicKey,
            },
          },
        }
      : undefined,
    normalizedConfig.keyword
      ? {
          post: buildCustomFeedKeywordPostFilter(normalizedConfig.keyword),
        }
      : undefined,
    normalizedConfig.timeframe.mode === "relative"
      ? {
          post: {
            timestamp: {
              greaterThanOrEqualTo: subtractRelativeTime({
                amount: normalizedConfig.timeframe.amount,
                now,
                unit: normalizedConfig.timeframe.unit,
              }),
            },
          },
        }
      : normalizedConfig.timeframe.mode === "range"
        ? hasNonEmptyFilterValue({
            ...(normalizedConfig.timeframe.from
              ? {
                  greaterThanOrEqualTo: normalizedConfig.timeframe.from,
                }
              : {}),
            ...(normalizedConfig.timeframe.to
              ? {
                  lessThanOrEqualTo: normalizedConfig.timeframe.to,
                }
              : {}),
          })
          ? {
              post: {
                timestamp: {
                  ...(normalizedConfig.timeframe.from
                    ? {
                        greaterThanOrEqualTo: normalizedConfig.timeframe.from,
                      }
                    : {}),
                  ...(normalizedConfig.timeframe.to
                    ? {
                        lessThanOrEqualTo: normalizedConfig.timeframe.to,
                      }
                    : {}),
                },
              },
            }
          : undefined
        : undefined,
    mediaFilters.length === 0
      ? undefined
      : mediaFilters.length === 1
        ? wrapPostFilterInPostStatsFilter(mediaFilters[0])
        : {
            or: mediaFilters
              .map((filter) => wrapPostFilterInPostStatsFilter(filter))
              .filter(Boolean) as PostStatFilter[],
          },
    wrapPostFilterInPostStatsFilter(
      getCustomFeedRepostFilter(normalizedConfig),
    ),
    wrapPostFilterInPostStatsFilter(
      normalizedConfig.isGated ? GATED_FILTER : undefined,
    ),
    normalizedConfig.isNft
      ? {
          post: {
            isNft: {
              equalTo: true,
            },
          },
        }
      : undefined,
    advancedSectionsFilter,
  );
};

export const buildCustomFeedPostStatsCondition = (
  config: CustomFeedQueryConfig,
): PostStatCondition | undefined => {
  const normalizedConfig = normalizeCustomFeedQueryConfig(config);
  const condition: PostStatCondition = {};
  const topLevelCanLiftConditions =
    normalizedConfig.advancedSections.length === 1 &&
    normalizedConfig.advancedSections.every(
      (section) => section.matchMode === "and",
    );

  if (topLevelCanLiftConditions) {
    for (const section of normalizedConfig.advancedSections) {
      for (const rule of section.rules) {
        if (
          !isConditionEligibleCustomFeedPostStatsRule(
            rule,
            section.matchMode,
            true,
          )
        ) {
          continue;
        }

        const definition = getCustomFeedRuleDefinition(rule.field);

        if (!definition.postStatConditionField) {
          continue;
        }

        condition[definition.postStatConditionField] = rule.value as never;
      }
    }
  }

  return Object.keys(condition).length > 0 ? condition : undefined;
};

export const getCustomFeedPostsOrderBy = (
  config: CustomFeedQueryConfig,
): PostsOrderBy[] => {
  return normalizeCustomFeedQueryConfig(config).sortBy.filter(
    isPostsOrderByValue,
  );
};

export const getCustomFeedPostStatsOrderBy = (
  config: CustomFeedQueryConfig,
): PostStatsOrderBy[] => {
  return normalizeCustomFeedQueryConfig(config).sortBy.filter(
    isPostStatsOrderByValue,
  );
};

export const getCustomFeedEditorSortOptionsForSource = (
  source: CustomFeedQuerySource,
) => {
  return CUSTOM_FEED_EDITOR_SORT_OPTIONS.filter((option) => {
    return option.source === source;
  });
};

export const FEED_QUERY_PRESET_REGISTRY: Record<
  FeedQueryPresetId,
  FeedQueryPresetDefinition
> = {
  trending: {
    id: "trending",
    label: "Trending",
    description: "Popular posts from the last week.",
    orderBy: [
      PostsOrderBy.UpvoteWealthUsdCentsDesc,
      PostsOrderBy.TotalDiamondValueUsdCentsDesc,
      PostsOrderBy.TimestampDesc,
    ],
    allowCustomOrderBy: false,
    buildFilter: ({ now } = {}) => ({
      timestamp: {
        greaterThanOrEqualTo: getRecentTimestamp({
          now,
          days: 7,
        }),
      },
      ...TRENDING_ENGAGEMENT_FILTER,
    }),
  },
  new: {
    id: "new",
    label: "New",
    description: "Most recent posts across Focus.",
    orderBy: PostsOrderBy.TimestampDesc,
    buildFilter: () => undefined,
  },
  images: {
    id: "images",
    label: "Images",
    description: "Posts with public or gated images.",
    orderBy: PostsOrderBy.TimestampDesc,
    buildFilter: () => IMAGES_FILTER,
  },
  videos: {
    id: "videos",
    label: "Videos",
    description: "Posts with public or gated videos.",
    orderBy: PostsOrderBy.TimestampDesc,
    buildFilter: () => VIDEOS_FILTER,
  },
  audio: {
    id: "audio",
    label: "Audio",
    description: "Posts with public or gated audio.",
    orderBy: PostsOrderBy.TimestampDesc,
    buildFilter: () => AUDIO_FILTER,
  },
  nfts: {
    id: "nfts",
    label: "NFTs",
    description: "Posts that were minted as NFTs.",
    orderBy: PostsOrderBy.TimestampDesc,
    buildFilter: () => NFTS_FILTER,
  },
};

export const FEED_QUERY_PRESET_ORDER: FeedQueryPresetId[] = [
  "trending",
  "new",
  "images",
  "videos",
  "audio",
  "nfts",
];

export const isFeedQueryPresetId = (
  value: unknown,
): value is FeedQueryPresetId => {
  return typeof value === "string" && value in FEED_QUERY_PRESET_REGISTRY;
};

export const getFeedQueryPreset = (id: FeedQueryPresetId) => {
  return FEED_QUERY_PRESET_REGISTRY[id];
};
